import React, { useMemo } from 'react';

import { Button, DropdownMenu } from '@bringg/react-components';
import { CellProps, Column } from 'react-table';
import moment from 'moment-timezone';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { ApplicationUuid, ExclusionType, ExclusionWindow, PrivilegeTypes } from '@bringg/types';
import { useTranslation } from 'react-i18next';

import { useHasFeatureFlag, useStores } from 'bringg-web';
import { useEnableImprovedExclusionWindowsUx } from './use-enable-improved-exclusion-windows-ux';
import { useHasAccess } from 'bringg-web/utils/privileges';
import { NO_TEAM } from 'bringg-web/features/planned-delivery-windows/planned-delivery-windows-view';

import styles from './exclusion-window-table.module.scss';

export interface UseGetExclusionWindowsTableColumnsParams {
	format: string;
	timezone: string;
	selectedTeamId: number;
	openExclusionWindowModalWithEditMode: (id: number) => void;
	onDeleteOpenConfirmModal: (id: number) => void;
}

export const useGetExclusionWindowsTableColumns = ({
	format,
	timezone,
	selectedTeamId,
	openExclusionWindowModalWithEditMode,
	onDeleteOpenConfirmModal
}: UseGetExclusionWindowsTableColumnsParams): Column<ExclusionWindow>[] => {
	const { t } = useTranslation();
	const useSlotsAndBlackoutsPrivilegesFeatureFlag = useHasFeatureFlag('use_slots_and_blackouts_privileges');
	const hasBetterExclusionWindowsUX = useEnableImprovedExclusionWindowsUx();
	const hasAccessToEditByPrivilege = useHasAccess(PrivilegeTypes.EDIT_EXCLUSION_WINDOWS);
	const isEditAllowed = useSlotsAndBlackoutsPrivilegesFeatureFlag ? hasAccessToEditByPrivilege : true;

	const { serviceAreasStore, servicePlansStore, applicationStore } = useStores();
	const showTasksLimit = !!applicationStore.getApplication(ApplicationUuid.ExclusionWindowTasksLimitApp);

	return useMemo(() => {
		const items: Column<ExclusionWindow>[] = [
			{
				Header: t<string>('EXCLUSION_WINDOWS_TABLE_HEADER.EXCLUSION_NAME'),
				accessor: 'name',
				Cell: ({ value }) => <span data-test-id="exclusion-table-name">{value}</span>
			},
			{
				Header: t<string>('EXCLUSION_WINDOWS_TABLE_HEADER.EFFECTIVE_DATES'),
				minWidth: 285,
				Cell: ({ row }: CellProps<ExclusionWindow>) => {
					const isRecurring = row.original.repeat;
					const dateFormat =
						isRecurring && hasBetterExclusionWindowsUX
							? format.replace(/\/[YyLl]+[,\s]*/g, ', ').trim()
							: format;

					return (
						<span
							data-test-id="exclusion-table-effective-dates"
							className={styles.breakWordCell}
						>{`${moment(row.original.start_time).tz(timezone).format(dateFormat)} - ${moment(
							row.original.end_time
						)
							.tz(timezone)
							.format(dateFormat)}`}</span>
					);
				}
			},
			...(hasBetterExclusionWindowsUX
				? [
						{
							Header: t<string>('EXCLUSION_WINDOWS_TABLE_HEADER.SERVICE_AREAS'),
							Cell: ({ row }: CellProps<ExclusionWindow>) => {
								const serviceAreaIds = row.original.service_area_ids || [];
								if (serviceAreaIds.length === 0) {
									return <span data-test-id="exclusion-table-service-areas"></span>;
								}

								const serviceAreaNames = serviceAreaIds
									.map(id => {
										const area = serviceAreasStore.serviceAreas.get(id);
										return area?.name || '';
									})
									.filter(name => name !== '')
									.join(', ');

								return <span data-test-id="exclusion-table-service-areas">{serviceAreaNames}</span>;
							}
						},
						{
							Header: t<string>('EXCLUSION_WINDOWS_TABLE_HEADER.SERVICE_PLAN'),
							maxWidth: 120,
							Cell: ({ row }: CellProps<ExclusionWindow>) => {
								const servicePlanId = row.original.service_plan_id;
								if (!servicePlanId) {
									return <span data-test-id="exclusion-table-service-plan"></span>;
								}

								const servicePlan = servicePlansStore.get(servicePlanId);
								return (
									<span data-test-id="exclusion-table-service-plan">{servicePlan?.name || ''}</span>
								);
							}
						},
						{
							Header: t<string>('EXCLUSION_WINDOWS_TABLE_HEADER.EXCLUSION_TYPE'),
							Cell: ({ row }: CellProps<ExclusionWindow>) => {
								const exclusionType = row.original.exclusion_type || ExclusionType.ALL;
								const exclusionTypeKey = ExclusionType[exclusionType];
								return (
									<span data-test-id="exclusion-table-exclusion-type">
										{t(`EXCLUSION_WINDOW_MODAL_TYPE.${exclusionTypeKey.toLocaleUpperCase()}`)}
									</span>
								);
							}
						},
						{
							Header: t<string>('EXCLUSION_WINDOWS_TABLE_HEADER.REPEAT'),
							maxWidth: 90,
							Cell: ({ row }: CellProps<ExclusionWindow>) => {
								return (
									<span data-test-id="exclusion-table-repeat">
										{row.original.repeat ? t('EXCLUSION_WINDOWS_TABLE.RECURRING') : ''}
									</span>
								);
							}
						},
						...(showTasksLimit
							? [
									{
										Header: t<string>('EXCLUSION_WINDOWS_TABLE_HEADER.MAX_ORDERS'),
										maxWidth: 90,
										Cell: ({ row }: CellProps<ExclusionWindow>) => {
											const tasksLimit = row.original.tasks_limit;
											return (
												<span data-test-id="exclusion-table-max-orders">
													{tasksLimit !== undefined && tasksLimit !== null ? tasksLimit : ''}
												</span>
											);
										}
									} as Column<ExclusionWindow>
							  ]
							: [])
				  ]
				: []),
			{
				Header: t<string>('EXCLUSION_WINDOWS_TABLE_HEADER.ORIGIN'),
				accessor: 'team_id',
				Cell: ({ value }) => {
					return (
						<span data-test-id="exclusion-table-origin">
							{value !== null
								? t('EXCLUSION_WINDOWS_TABLE.TEAM_TEXT')
								: t('EXCLUSION_WINDOWS_TABLE.MERCHANT_TEXT')}
						</span>
					);
				}
			}
		];

		if (isEditAllowed) {
			const actionItem: Column<ExclusionWindow> = {
				Header: t<string>('EXCLUSION_WINDOWS_TABLE_HEADER.ACTIONS'),
				Cell: ({ row }: CellProps<ExclusionWindow>) => {
					const onClickEdit = () => openExclusionWindowModalWithEditMode(row.original.id);
					const onClickDelete = () => onDeleteOpenConfirmModal(row.original.id);
					const hasAccessToEditByOrigin =
						selectedTeamId === NO_TEAM ? !row.original.team_id : row.original.team_id;

					return (
						<DropdownMenu
							items={[
								{
									key: '1',
									label: (
										<span data-test-id="exclusion-table-actions-edit">
											{t('EXCLUSION_WINDOWS_VIEW_BUTTON_DROPDOWN.EDIT_TEXT')}
										</span>
									),
									onClick: onClickEdit,
									disabled: !hasAccessToEditByPrivilege || !hasAccessToEditByOrigin
								},
								{
									key: '2',
									label: (
										<span data-test-id="exclusion-table-actions-delete">
											{t('EXCLUSION_WINDOWS_VIEW_BUTTON_DROPDOWN.DELETE_TEXT')}
										</span>
									),
									onClick: onClickDelete,
									disabled: !hasAccessToEditByPrivilege || !hasAccessToEditByOrigin
								}
							]}
							trigger={['click']}
						>
							<Button
								type="text"
								disabled={!hasAccessToEditByPrivilege}
								icon={<BringgIcon iconName={BringgFontIcons.MenuHorizontal} />}
								data-test-id="exclusion-table-actions"
							></Button>
						</DropdownMenu>
					);
				}
			};

			if (hasBetterExclusionWindowsUX) {
				actionItem.width = 70;
				actionItem.maxWidth = 70;
			} else {
				actionItem.width = 50;
			}

			items.push(actionItem);
		}

		return items;
	}, [
		hasBetterExclusionWindowsUX,
		showTasksLimit,
		isEditAllowed,
		format,
		timezone,
		serviceAreasStore.serviceAreas,
		servicePlansStore,
		selectedTeamId,
		hasAccessToEditByPrivilege,
		openExclusionWindowModalWithEditMode,
		onDeleteOpenConfirmModal
	]);
};
